import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { post } from '../../../redux/api';
import type { CreateFossabotPRRequest } from '../../../../shared/fossabot/request';
import type { CreateFossabotPRResponse } from '../../../../shared/fossabot/response';
import type { ErrorResponse } from '../../../ReactQuery/errors';

import { getFossabotIssueQueryKey } from './useFossabotPRStatus';

type CreateFossabotPRVariables = CreateFossabotPRRequest & {
  /** The FOSSA issue to open a PR for. */
  issueId: number;
};

const createFossabotPR = async ({ issueId, ...body }: CreateFossabotPRVariables) => {
  const response = await post<CreateFossabotPRRequest, CreateFossabotPRResponse>(
    `/api/fossabot/issues/${issueId}/dependency-upgrade-pr`,
    body,
  );
  return response.data as CreateFossabotPRResponse;
};

/**
 * Asks fossabot to open a dependency-upgrade PR for an issue. Creation is
 * asynchronous: the response usually comes back as 'creating' with a jobId,
 * and useFossabotPRStatus polls it to completion — so success has no toast
 * (the box's 'creating' state is the feedback) and only failures do.
 *
 * The issue id is a mutation variable rather than a hook argument, so callers
 * that render before the issue loads have nothing to pass until they do.
 */
export function useCreateFossabotPR() {
  return useToastedMutation<CreateFossabotPRResponse, ErrorResponse, CreateFossabotPRVariables>({
    mutationFn: createFossabotPR,
    errorToastMessage: 'Failed to create pull request.',
    onSuccess: (_data, { issueId }) => {
      // Refetch the issue's PR-status query so the box flips to 'creating'.
      queryClient.invalidateQueries({ queryKey: getFossabotIssueQueryKey(issueId) });
    },
  });
}
